/**
 * MarketIntelPanel — right panel showing benchmark pricing from government bid data.
 */
import {
    Box,
    VStack,
    HStack,
    Text,
    Icon,
    Skeleton,
    Badge,
    Tooltip,
    useColorModeValue,
} from '@chakra-ui/react';

import { MdTrendingUp, MdInfo } from 'react-icons/md';
import { useMarketIntel } from '@/services/hooks';

interface Props {
    setId: number | null;
}

const fmt = (n: number | null) =>
    n == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(n);

function PriceRangeBar({ low, high, yours }: { low: number; high: number; yours: number }) {
    const trackBg = useColorModeValue('gray.200', 'navy.600');
    const labelColor = useColorModeValue('gray.500', 'gray.500');

    const range = high - low;
    if (range <= 0) return null;

    const pct = Math.min(100, Math.max(0, ((yours - low) / range) * 100));
    const color = pct < 50 ? 'green.400' : pct < 80 ? 'yellow.400' : 'red.400';

    return (
        <Box position="relative" mt={2}>
            {/* Track */}
            <Box
                h="6px"
                borderRadius="full"
                bg={trackBg}
                position="relative"
                overflow="visible"
            >
                {/* Fill */}
                <Box
                    position="absolute"
                    left={0}
                    w={`${pct}%`}
                    h="100%"
                    borderRadius="full"
                    bg={color}
                    transition="width 0.4s ease"
                />
            </Box>
            {/* Labels */}
            <HStack justify="space-between" mt={1}>
                <Text fontSize="9px" color={labelColor}>{fmt(low)}</Text>
                <Text fontSize="9px" color={labelColor}>{fmt(high)}</Text>
            </HStack>
        </Box>
    );
}

function BenchmarkCard({ item }: { item: any }) {
    const hasData = item.sample_count >= 1;
    const priceDelta = hasData && item.market_median
        ? ((item.your_price - item.market_median) / item.market_median) * 100
        : null;

    let statusColor = 'gray';
    let statusLabel = '—';
    if (priceDelta !== null) {
        if (priceDelta < -10) { statusColor = 'green'; statusLabel = 'Below market'; }
        else if (priceDelta <= 10) { statusColor = 'yellow'; statusLabel = 'At market'; }
        else { statusColor = 'red'; statusLabel = 'Above market'; }
    }

    const cardBg = useColorModeValue('white', 'navy.700');
    const titleColor = useColorModeValue('gray.800', 'gray.100');
    const primaryTextColor = useColorModeValue('gray.800', 'white');
    const secondaryTextColor = useColorModeValue('gray.500', 'gray.400');
    const subtleTextColor = useColorModeValue('gray.400', 'gray.500');
    const sourceNoteColor = useColorModeValue('gray.500', 'gray.600');
    const cardBorder = useColorModeValue('gray.200', 'transparent');

    return (
        <Box
            px={3}
            py={3}
            borderRadius="lg"
            bg={cardBg}
            border="1px solid"
            borderColor={cardBorder}
            borderLeftWidth="3px"
            borderLeftColor={`${statusColor}.400`}
            boxShadow={useColorModeValue('sm', 'none')}
        >
            <HStack justify="space-between" mb={1}>
                <Text fontSize="xs" fontWeight="700" color={titleColor} noOfLines={1} flex={1}>
                    {item.name}
                </Text>
                {priceDelta !== null && (
                    <Badge colorScheme={statusColor} variant="subtle" fontSize="9px">
                        {statusLabel}
                    </Badge>
                )}
            </HStack>

            <Text fontSize="xs" color={secondaryTextColor} mb={2}>
                Your price: <Text as="span" color={primaryTextColor} fontWeight="600">{fmt(item.your_price)}</Text>
                <Text as="span" color={subtleTextColor}> / {item.uom}</Text>
            </Text>

            {!hasData ? (
                <Text fontSize="xs" color={subtleTextColor} fontStyle="italic">
                    No market data available for this item
                </Text>
            ) : (
                <>
                    <HStack spacing={4} mb={1}>
                        <Box>
                            <Text fontSize="9px" color={subtleTextColor} textTransform="uppercase" letterSpacing="wider">Low</Text>
                            <Text fontSize="xs" color="green.500" fontWeight="600">{fmt(item.market_low)}</Text>
                        </Box>
                        <Box>
                            <Text fontSize="9px" color={subtleTextColor} textTransform="uppercase" letterSpacing="wider">Median</Text>
                            <Text fontSize="xs" color={primaryTextColor} fontWeight="600">{fmt(item.market_median)}</Text>
                        </Box>
                        <Box>
                            <Text fontSize="9px" color={subtleTextColor} textTransform="uppercase" letterSpacing="wider">High</Text>
                            <Text fontSize="xs" color="red.500" fontWeight="600">{fmt(item.market_high)}</Text>
                        </Box>
                    </HStack>
                    {item.market_low != null && item.market_high != null && (
                        <PriceRangeBar
                            low={item.market_low}
                            high={item.market_high}
                            yours={item.your_price}
                        />
                    )}
                    {item.source_note && (
                        <Text fontSize="9px" color={sourceNoteColor} mt={2}>{item.source_note}</Text>
                    )}
                </>
            )}
        </Box>
    );
}

export function MarketIntelPanel({ setId }: Props) {
    const { data, isLoading } = useMarketIntel(setId);

    const bgVal = useColorModeValue('gray.50', 'navy.800');
    const borderVal = useColorModeValue('gray.200', 'navy.700');
    const headerTitleColor = useColorModeValue('gray.800', 'gray.200');
    const subtitleColor = useColorModeValue('gray.500', 'gray.500');
    const emptyIconColor = useColorModeValue('gray.300', 'gray.700');
    const emptyTextColor = useColorModeValue('gray.500', 'gray.600');
    const skeletonStart = useColorModeValue('gray.100', 'navy.700');
    const skeletonEnd = useColorModeValue('gray.200', 'navy.600');

    return (
        <Box
            h="100%"
            display="flex"
            flexDir="column"
            borderLeft="1px solid"
            borderColor={borderVal}
            bg={bgVal}
            overflowY="auto"
        >
            {/* Header */}
            <Box px={4} pt={5} pb={3} borderBottom="1px solid" borderColor={borderVal}>
                <HStack mb={1}>
                    <Icon as={MdTrendingUp} color="brand.500" boxSize={4} />
                    <Text fontWeight="700" fontSize="sm" color={headerTitleColor}>Market Intel</Text>
                    <Tooltip label="Benchmarked against government bid records in your database" hasArrow placement="left">
                        <Box>
                            <Icon as={MdInfo} color={subtitleColor} boxSize={3} cursor="help" />
                        </Box>
                    </Tooltip>
                </HStack>
                <Text fontSize="xs" color={subtitleColor}>
                    How your prices compare to public bid data
                </Text>
            </Box>

            {/* Content */}
            <VStack flex="1" overflowY="auto" spacing={2} align="stretch" px={3} py={3}>
                {!setId ? (
                    <Box textAlign="center" py={16}>
                        <Icon as={MdTrendingUp} boxSize={10} color={emptyIconColor} mb={3} />
                        <Text fontSize="xs" color={emptyTextColor}>Select a price set to see market intel</Text>
                    </Box>
                ) : isLoading ? (
                    [1, 2, 3].map((i) => (
                        <Skeleton key={i} height="90px" borderRadius="lg" startColor={skeletonStart} endColor={skeletonEnd} />
                    ))
                ) : !data || data.length === 0 ? (
                    <Box textAlign="center" py={10}>
                        <Text fontSize="xs" color={subtitleColor}>No items in this set yet</Text>
                    </Box>
                ) : (
                    data.map((item: any) => <BenchmarkCard key={item.line_item_id} item={item} />)
                )}
            </VStack>
        </Box>
    );
}
